import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRivhitData, rivhitDisplayName } from '@/context/RivhitDataContext';

interface CustomerOption {
  customer_id: string;
  name: string;
  name_he?: string;
  phone?: string;
  email?: string;
  confidence?: number;
}

interface CustomerSelectorCardProps {
  data: {
    suggestions: CustomerOption[];
    context?: string;
    allow_search?: boolean;
  };
  onSelect: (selection: Record<string, unknown>) => void;
}

export function CustomerSelectorCard({ data, onSelect }: CustomerSelectorCardProps) {
  const { t } = useTranslation();
  const { configured, searchCustomers: searchRivhit, ensureCustomersLoaded } = useRivhitData();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CustomerOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  // Debounced live search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setHighlightIdx(-1);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => {
      try {
        const results = searchRivhit(searchQuery).slice(0, 10).map((c) => ({
          customer_id: String(c.customer_id),
          name: rivhitDisplayName(c),
          phone: c.phone,
          email: c.email,
        }));
        setSearchResults(results);
        setHighlightIdx(-1);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, searchRivhit]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const handleSelect = (customer: CustomerOption) => {
    onSelect({
      customer_id: customer.customer_id,
      customer_name: customer.name,
    });
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const list = searchResults.length > 0 ? searchResults : [];
      if (!list.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx((prev) => (prev < list.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx((prev) => (prev > 0 ? prev - 1 : list.length - 1));
      } else if (e.key === 'Enter' && highlightIdx >= 0) {
        e.preventDefault();
        handleSelect(list[highlightIdx]);
      }
    },
    [searchResults, highlightIdx],
  );

  const suggestions = data.suggestions || [];
  const showSearchResults = searchQuery.trim().length > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden my-2">
      {data.context && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500">
          {data.context}
        </div>
      )}
      <div className="p-3">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {t('agentChat.cards.selectCustomer', 'Select a customer:')}
        </p>

        {/* Best guesses */}
        {suggestions.length > 0 && !showSearchResults && (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 mb-1.5">
              ⭐ {t('agentChat.cards.bestGuesses', 'Best guesses')}
            </p>
            <div className="space-y-1.5">
              {suggestions.map((c) => (
                <button
                  key={c.customer_id}
                  onClick={() => handleSelect(c)}
                  className="w-full flex items-center justify-between p-2.5 rounded-lg border border-primary-200 bg-primary-50/40 hover:border-primary-400 hover:bg-primary-50 transition-colors text-left"
                >
                  <div>
                    <span className="text-sm font-medium text-gray-900">{c.name}</span>
                    {c.name_he && <span className="text-sm text-gray-500 ml-1">({c.name_he})</span>}
                    <div className="flex gap-3 mt-0.5">
                      {c.phone && <span className="text-xs text-gray-400">☎ {c.phone}</span>}
                      {c.email && <span className="text-xs text-gray-400">✉ {c.email}</span>}
                    </div>
                  </div>
                  {c.confidence != null && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      c.confidence > 0.8 ? 'bg-green-100 text-green-700' :
                      c.confidence > 0.5 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {Math.round(c.confidence * 100)}%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search input — always visible */}
        {data.allow_search !== false && (
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('agentChat.cards.searchPlaceholder', 'Search by name, phone, email...')}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  dir="auto"
                />
              </div>
            </div>

            {/* Live search results dropdown */}
            {showSearchResults && (
              <div
                ref={listRef}
                className="mt-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg"
              >
                {isSearching ? (
                  <div className="flex items-center justify-center py-3 text-xs text-gray-400">
                    <span className="inline-block w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mr-2" />
                    {t('common.searching', 'Searching...')}
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="py-3 text-center text-xs text-gray-400">
                    {t('common.noResults', 'No results found')}
                  </div>
                ) : (
                  searchResults.map((c, idx) => (
                    <button
                      key={c.customer_id}
                      onClick={() => handleSelect(c)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors border-b border-gray-50 last:border-0 ${
                        idx === highlightIdx ? 'bg-primary-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div>
                        <span className="text-sm font-medium text-gray-900">{c.name}</span>
                        <div className="flex gap-3 mt-0.5">
                          {c.phone && <span className="text-xs text-gray-400">☎ {c.phone}</span>}
                          {c.email && <span className="text-xs text-gray-400">✉ {c.email}</span>}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
